/**
 * Decides whether the chat is on offer, and keeps that decision current.
 *
 * Two conditions: the user is logged in (never the login page), and customer
 * service is closed — the chatbot is an out-of-hours channel and the chatbot
 * service owns that schedule (`isChatAvailable`). A performer stays logged in
 * across the opening boundary, so the answer is re-checked on a poll rather than
 * decided once per login.
 *
 * Starting a fresh chat session per login is handled entirely on the chatbot side
 * via the members-API JWT's per-login `lsid` claim — a new login yields a new
 * claim, so the chatbot closes the previous conversation and starts a new one in
 * the current language; reloads within a login resume it. Nothing is needed here.
 */
import { watch } from 'vue';

import config from '@/config';
import { useAuthStore } from '@/stores/authentication';

import { chatWidgetLoaded, isChatAvailable, isChatPanelOpen, loadChatWidget, unloadChatWidget } from './chatWidget';

export const CHAT_AVAILABILITY_POLL_MS = 5 * 60 * 1000;

export async function syncChatWidget(): Promise<void> {
    const authStore = useAuthStore();

    if (!authStore.isLoggedIn()) {
        unloadChatWidget();
        return;
    }

    const available = await isChatAvailable(config.ChatWidgetOrigin);

    // Re-read the session after the await: a logout during that round-trip would
    // otherwise re-inject the widget onto the login page until the next poll.
    if (!authStore.isLoggedIn()) {
        unloadChatWidget();
        return;
    }

    if (available) {
        loadChatWidget(config.ChatWidgetOrigin, authStore.account?.language || config.DefaultLanguage);
        return;
    }

    // Business hours opening is a clock event, not something the user did, so stop
    // offering the chat without destroying one in progress: hiding the icon closes
    // the channel, and the DOM is swept on the first poll that finds the panel
    // closed. Tearing it out on the tick would take a half-typed message with it,
    // at the one moment the out-of-hours channel is most likely to be in use.
    chatWidgetLoaded.value = false;
    if (!isChatPanelOpen()) {
        unloadChatWidget();
    }
}

export function startChatWidgetSync(): void {
    const authStore = useAuthStore();

    // Two getters rather than one object literal: a fresh object compares unequal
    // every time, and each firing costs an availability request.
    watch([() => authStore.isLoggedIn(), () => authStore.account?.language], syncChatWidget, { immediate: true });
    setInterval(syncChatWidget, CHAT_AVAILABILITY_POLL_MS);
}
